import React, { useState, useEffect, useRef, useCallback } from 'react'
import { MessageCircle, X, Send, Plus, Users, ArrowLeft, Trash2, Image } from 'lucide-react'
import toast from 'react-hot-toast'
import api from '../api/axios'
import { useAuth } from '../context/AuthContext'
import { useNavigate } from 'react-router-dom'

interface Message {
  id: string
  roomId: string
  senderId: string
  senderName: string
  senderUsername: string | null
  messageText: string | null
  imageUrl: string | null
  isDeleted: boolean
  isMine: boolean
  createdAt: string
  isRead: boolean
  reactions?: Record<string, { count: number, users: string[], isMine: boolean }>
  replyToMessage?: {
    id: string
    messageText: string | null
    senderName: string
    imageUrl: string | null
  } | null
}

interface Room {
  id: string
  name: string
  type: 'direct' | 'group'
  members: { id: string, name: string, username: string | null, roleName: string }[]
  lastMessage: { text: string, senderName: string, createdAt: string } | null
  unreadCount: number
  otherUser: { id: string, name: string, isOnline?: boolean } | null
}

interface StaffMember {
  id: string
  name: string
  username: string | null
  roleName: string
  isOnline: boolean
}

export default function ChatWidget() {
  const { user } = useAuth()
  const [open, setOpen] = useState(false)
  const [view, setView] = useState<'rooms' | 'chat' | 'new-dm' | 'new-group'>('rooms')
  const [rooms, setRooms] = useState<Room[]>([])
  const [activeRoom, setActiveRoom] = useState<Room | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const lastMessageCountRef = useRef<number>(0)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [text, setText] = useState('')
  const [replyingTo, setReplyingTo] = useState<Message | null>(null)
  const [showScrollToBottom, setShowScrollToBottom] = useState(false)
  const [showMembers, setShowMembers] = useState(false)
  const [sending, setSending] = useState(false)
  const [unreadTotal, setUnreadTotal] = useState(0)
  const [staff, setStaff] = useState<StaffMember[]>([])
  const [groupName, setGroupName] = useState('')
  const [selectedMembers, setSelectedMembers] = useState<string[]>([])
  const [loadingMessages, setLoadingMessages] = useState(false)
  const [typingUsers, setTypingUsers] = useState<string[]>([])
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const [hoveredMsgId, setHoveredMsgId] = useState<string | null>(null)
  const [showSearch, setShowSearch] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [pinnedMessages, setPinnedMessages] = useState<any[]>([])
  const [showPins, setShowPins] = useState(false)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const emojiPickerRef = useRef<HTMLDivElement>(null)
  const [mentionQuery, setMentionQuery] = useState('')
  const [mentionSuggestions, setMentionSuggestions] = useState<any[]>([])
  const [showMentions, setShowMentions] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const EMOJIS = [
    '😀','😂','😊','😍','🥰','😎','😢','😭','😡','🤔',
    '👍','👎','👏','🙏','🤝','✌️','💪','🔥','❤️','💯',
    '✅','❌','⚠️','📎','📝','🎉','🎨','⭐','💡','🚀',
    '😅','🤣','😇','🥳','😴','🤯','😱','🙄','😏','🤗',
    '😋','😜','🤪','😘','🥹','😤','😔','😪','🤒','🤕',
    '🤑','🤠','🥸','😈','👻','💀','🤡','👽','🤖','💩',
    '👋','🤙','👌','🤌','🤏','🫶','🫂','💅','🦾','🦿',
    '🌟','💥','✨','💫','🎯','🏆','🥇','🎖️','🏅','🎗️',
    '🍕','🍔','☕','🧃','🍰','🎂','🍩','🍪','🍫','🍭',
    '🐶','🐱','🦊','🐻','🐼','🦁','🐯','🐨','🐸','🦄',
    '🌈','⚡','❄️','🌊','🌸','🌺','🌻','🍀','🌴','🔮',
    '💎','🔑','🗝️','🔒','📱','💻','🖥️','🖨️','⌨️','🖱️',
    '📸','🎥','🎬','🎮','🎲','🎭','🎪','🎠','🎡','🎢',
    '🚗','🚀','✈️','🚢','🚂','🏍️','🚁','🛸','⛵','🚤',
  ]
  const prevUnreadRef = useRef(0)

const playNotificationSound = () => {
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)()

    // First tone
    const osc1 = ctx.createOscillator()
    const gain1 = ctx.createGain()
    osc1.connect(gain1)
    gain1.connect(ctx.destination)
    osc1.type = 'sine'
    osc1.frequency.setValueAtTime(1000, ctx.currentTime)
    gain1.gain.setValueAtTime(1.0, ctx.currentTime)
    gain1.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.2)
    osc1.start(ctx.currentTime)
    osc1.stop(ctx.currentTime + 0.2)

    // Second tone (slightly higher pitch — pleasant ding)
    const osc2 = ctx.createOscillator()
    const gain2 = ctx.createGain()
    osc2.connect(gain2)
    gain2.connect(ctx.destination)
    osc2.type = 'sine'
    osc2.frequency.setValueAtTime(1400, ctx.currentTime + 0.15)
    gain2.gain.setValueAtTime(0, ctx.currentTime)
    gain2.gain.setValueAtTime(1.0, ctx.currentTime + 0.15)
    gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5)
    osc2.start(ctx.currentTime + 0.15)
    osc2.stop(ctx.currentTime + 0.5)
  } catch {}
}
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const pollRef = useRef<NodeJS.Timeout | null>(null)
  const [jobSuggestions, setJobSuggestions] = useState<any[]>([])
  const [showJobSuggestions, setShowJobSuggestions] = useState(false)
  const navigate = useNavigate()

  const fetchRooms = useCallback(async () => {
    try {
      const res = await api.get('/chat/rooms')
      setRooms(res.data)
    } catch {}
  }, [])

  const fetchUnread = useCallback(async () => {
    try {
      const res = await api.get('/chat/unread')
      setUnreadTotal(res.data.count)
    } catch {}
  }, [])

  const fetchMessages = useCallback(async (roomId: string) => {
    try {
      const res = await api.get(`/chat/rooms/${roomId}/messages`)
      setMessages(res.data)
    } catch {}
  }, [])

  const lastPollTime = useRef<string>(new Date().toISOString())

  // Optimized single poll endpoint
  useEffect(() => {
    fetchUnread()
    const interval = setInterval(async () => {
      if (activeRoom) {
        try {
          const res = await api.get(`/chat/rooms/${activeRoom.id}/poll?since=${lastPollTime.current}`)
          lastPollTime.current = new Date().toISOString()
          // Append new messages
          if (res.data.messages?.length > 0) {
            setMessages(prev => {
              const existingIds = new Set(prev.map((m: Message) => m.id))
              const newMsgs = res.data.messages.filter((m: Message) => !existingIds.has(m.id))
              return [...prev, ...newMsgs]
            })
          }
          // Update typing
          setTypingUsers(res.data.typing || [])
          // Update unread count
          setUnreadTotal(res.data.unread || 0)
        } catch {}
      } else {
        fetchUnread()
        fetchRooms()
      }
    }, 5000)
    pollRef.current = interval
    return () => clearInterval(interval)
  }, [activeRoom, fetchUnread, fetchRooms])

  useEffect(() => {
    if (open) fetchRooms()
  }, [open, fetchRooms])

  useEffect(() => {
    if (activeRoom) {
      api.get(`/chat/rooms/${activeRoom.id}/pins`).then(res => setPinnedMessages(res.data)).catch(() => {})
    } else {
      setPinnedMessages([])
    }
  }, [activeRoom?.id])

  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const initialScrollDone = useRef(false)

  useEffect(() => {
    initialScrollDone.current = false
  }, [activeRoom?.id])

  useEffect(() => {
    if (!messages.length) return
    const container = scrollContainerRef.current
    if (!container) return

    // First load — scroll to bottom smoothly
    if (!initialScrollDone.current) {
      initialScrollDone.current = true
      setTimeout(() => {
        container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' })
      }, 100)
      return
    }

    // Subsequent updates — only scroll if already near bottom
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight
    if (distanceFromBottom < 150) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages])

  useEffect(() => {
  if (unreadTotal > prevUnreadRef.current && !open) {
    playNotificationSound()
  }
  prevUnreadRef.current = unreadTotal
}, [unreadTotal, open])

useEffect(() => {
  const handleClickOutside = (e: MouseEvent) => {
    if (emojiPickerRef.current && !emojiPickerRef.current.contains(e.target as Node)) {
      setShowEmojiPicker(false)
    }
  }
  document.addEventListener('mousedown', handleClickOutside)
  return () => document.removeEventListener('mousedown', handleClickOutside)
}, [])

  const openRoom = async (room: Room) => {
    setActiveRoom(room)
    setView('chat')
    setLoadingMessages(true)
    await fetchMessages(room.id)
    setLoadingMessages(false)
    fetchRooms()
  }

  const handleSend = async () => {
    if (!text.trim() || !activeRoom) return
    setSending(true)
    try {
      // Extract mentioned names from message
      const mentionMatches = text.match(/@([\w\s]+?)(?=\s@|\s*$)/g)
      const mentionedUserIds = mentionMatches ? staff
        .filter(s => mentionMatches.some(m => m.slice(1).trim().toLowerCase() === s.name.toLowerCase()))
        .map(s => s.id) : []

      const res = await api.post(`/chat/rooms/${activeRoom.id}/messages`, {
        messageText: text,
        mentionedUserIds,
        ...(replyingTo ? { replyToMessageId: replyingTo.id } : {})
      })
      setMessages(prev => [...prev, res.data])
      setText('')
      setReplyingTo(null)
      setShowMentions(false)
    } catch {}
    setSending(false)
  }

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !activeRoom) return

    if (file.size > 3 * 1024 * 1024) {
      alert('Image must be under 3MB')
      return
    }

    const formData = new FormData()
    formData.append('image', file)
    try {
      const res = await api.post(`/chat/rooms/${activeRoom.id}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      setMessages(prev => [...prev, res.data])
    } catch {}
    e.target.value = ''
  }

  const handleDelete = async (messageId: string) => {
    try {
      await api.delete(`/chat/messages/${messageId}`)
      setMessages(prev => prev.map(m => m.id === messageId ? { ...m, isDeleted: true, messageText: null, imageUrl: null } : m))
    } catch {}
  }

  const startDM = async (staffId: string) => {
    try {
      const res = await api.post(`/chat/rooms/direct/${staffId}`)
      await fetchRooms()
      const roomsRes = await api.get('/chat/rooms')
      setRooms(roomsRes.data)
      const room = roomsRes.data.find((r: Room) => r.id === res.data.roomId)
      if (room) openRoom(room)
    } catch {}
  }

  const createGroup = async () => {
    if (!groupName.trim() || selectedMembers.length === 0) return
    try {
      const res = await api.post('/chat/rooms/group', { name: groupName, memberIds: selectedMembers })
      await fetchRooms()
      const roomsRes = await api.get('/chat/rooms')
      setRooms(roomsRes.data)
      const room = roomsRes.data.find((r: Room) => r.id === res.data.roomId)
      if (room) openRoom(room)
      setGroupName('')
      setSelectedMembers([])
    } catch {}
  }

  const formatTime = (iso: string) => {
    const d = new Date(iso)
    const now = new Date()
    const isToday = d.toDateString() === now.toDateString()
    return isToday
      ? d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
      : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
  }

  const loadStaff = async () => {
    try {
      const res = await api.get('/chat/staff')
      setStaff(res.data)
    } catch {}
  }

  const handleTextChange = async (val: string) => {
    setText(val)

    // Typing indicator
    if (activeRoom && val.trim()) {
      api.post(`/chat/rooms/${activeRoom.id}/typing`).catch(() => {})
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current)
      typingTimeoutRef.current = setTimeout(() => {}, 3000)
    }

    // Job autocomplete with #
    const hashMatch = val.match(/#(\w{2,})$/)
    if (hashMatch) {
      const q = hashMatch[1]
      try {
        const res = await api.get(`/jobs/search/autocomplete?q=${q}`)
        setJobSuggestions(res.data)
        setShowJobSuggestions(res.data.length > 0)
      } catch {}
    } else {
      setShowJobSuggestions(false)
      setJobSuggestions([])
    }

    // Mention autocomplete with @
    const mentionMatch = val.match(/@(\w*)$/)
    if (mentionMatch) {
      const q = mentionMatch[1].toLowerCase()
      setMentionQuery(q)
      const filtered = staff.filter(s =>
        s.name.toLowerCase().includes(q) && s.id !== user?.userId?.toString()
      )
      setMentionSuggestions(filtered.slice(0, 6))
      setShowMentions(filtered.length > 0)
    } else {
      setShowMentions(false)
      setMentionSuggestions([])
      setMentionQuery('')
    }
  }

const selectJob = (job: any) => {
  const newText = text.replace(/#\w*$/, `#${job.jobCode} `)
  setText(newText)
  setShowJobSuggestions(false)
  setJobSuggestions([])
}

const selectMention = (member: any) => {
    const newText = text.replace(/@\w*$/, `@${member.name} `)
    setText(newText)
    setShowMentions(false)
    setMentionSuggestions([])
    textareaRef.current?.focus()
  }

const formatDateSeparator = (date: Date) => {
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(today.getDate() - 1)
  if (date.toDateString() === today.toDateString()) return 'Today'
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday'
  return date.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })
}

const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()

const renderMessage = (text: string) => {
  const parts = text.split(/(#[A-Z0-9\-]+|@[\w ]+)/g)
  return parts.map((part, i) => {
    if (part.startsWith('@')) {
      return <span key={i} className="bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-1 rounded font-medium">{part}</span>
    }
    if (part.match(/^#[A-Z0-9\-]+$/)) {
      return (
        <span key={i}
          onClick={async () => {
              try {
                const res = await api.get(`/jobs/search/autocomplete?q=${part.slice(1)}`)
                if (res.data.length > 0) {
                  setOpen(false)
                  navigate(`/jobs?openJob=${res.data[0].id}`)
                }
              } catch {}
            }}
          className="inline-block bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400 px-1.5 py-0.5 rounded-md text-xs font-mono font-semibold cursor-pointer hover:bg-orange-200 transition-colors mx-0.5">
          {part}
        </span>
      )
    }
    return <span key={i}>{part}</span>
  })
}

  if (user?.roleName === 'client') return null

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(o => !o)}
        className="fixed bottom-20 sm:bottom-6 right-6 z-50 w-14 h-14 bg-orange-500 hover:bg-orange-600 text-white rounded-2xl shadow-lg flex items-center justify-center transition-all hover:scale-105">
        {!open && unreadTotal > 0 && (
          <span className="absolute inset-0 rounded-2xl bg-orange-400 animate-ping opacity-30" />
        )}
        {open ? <X size={22} /> : <MessageCircle size={22} />}
        {!open && unreadTotal > 0 && (
          <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
            {unreadTotal > 99 ? '99+' : unreadTotal}
          </span>
        )}
      </button>

      {/* Chat panel */}
      {open && (
        <div className="fixed bottom-36 sm:bottom-24 right-6 z-50 w-80 sm:w-96 h-[520px] bg-white dark:bg-[#0d1e35] rounded-2xl shadow-2xl border border-gray-100 dark:border-[#1e3a5f] flex flex-col overflow-hidden" style={{ boxShadow: '0 20px 60px rgba(30,45,74,0.3)' }}>

          {/* Header */}
          <div className="bg-[#123D68] px-4 py-3 flex items-center gap-3 flex-shrink-0">
            {view !== 'rooms' && (
              <button onClick={() => { setView('rooms'); setActiveRoom(null) }} className="text-white/70 hover:text-white">
                <ArrowLeft size={18} />
              </button>
            )}
            <p className="flex-1 font-semibold text-white text-sm">
              {view === 'rooms' && 'InkConnect'}
              {view === 'chat' && (activeRoom?.name || 'Chat')}
              {view === 'new-dm' && 'New Message'}
              {view === 'new-group' && 'New Group'}
            </p>
            {view === 'rooms' && (
              <div className="flex items-center gap-2">
                <button onClick={() => { setView('new-dm'); loadStaff() }} className="text-white/70 hover:text-white p-1 rounded-lg hover:bg-white/10">
                  <Plus size={16} />
                </button>
                {user?.permissions?.manage_chat_groups && (
                  <button onClick={() => { setView('new-group'); loadStaff() }} className="text-white/70 hover:text-white p-1 rounded-lg hover:bg-white/10">
                    <Users size={16} />
                  </button>
                )}
              </div>
            )}
            {view === 'chat' && (
              <div className="flex items-center gap-2">
                {activeRoom?.type === 'group' && (
                  <button onClick={() => setShowMembers(s => !s)}
                    className={`text-xs px-2 py-1 rounded-lg transition-colors ${showMembers ? 'bg-white/20 text-white' : 'text-white/50 hover:text-white hover:bg-white/10'}`}>
                    👥 {activeRoom.members.length} members
                  </button>
                )}
                <button onClick={() => { setShowSearch(s => !s); setSearchQuery('') }}
                  className="text-white/70 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-colors">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                  </svg>
                </button>
                {pinnedMessages.length > 0 && (
                  <button onClick={() => setShowPins(s => !s)}
                    className={`p-1 rounded-lg transition-colors ${showPins ? 'text-yellow-300 bg-white/10' : 'text-white/70 hover:text-white hover:bg-white/10'}`}
                    title={`${pinnedMessages.length} pinned message${pinnedMessages.length > 1 ? 's' : ''}`}>
                    📌
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Members panel */}
          {view === 'chat' && showMembers && activeRoom?.type === 'group' && (
            <div className="bg-[#0d2d4f] border-b border-white/10 flex-shrink-0 max-h-40 overflow-y-auto">
              <div className="px-4 py-2 flex items-center justify-between border-b border-white/5">
                <p className="text-[10px] font-semibold text-white/40 uppercase tracking-wide">Group Members</p>
                <button onClick={() => setShowMembers(false)} className="text-white/30 hover:text-white/70 text-xs">✕</button>
              </div>
              <div className="grid grid-cols-2 gap-0">
                {activeRoom.members.map((m: any) => (
                  <div key={m.id} className="flex items-center gap-2 px-4 py-2 hover:bg-white/5 transition-colors group/member">
                    <div className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0">
                      {m.name[0]?.toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-white text-xs font-medium truncate">{m.name}</p>
                      <p className="text-white/30 text-[10px] capitalize truncate">{m.roleName}</p>
                    </div>
                    {m.id !== user?.userId?.toString() && (
                      <button
                        onClick={async () => {
                          if (!window.confirm(`Remove ${m.name} from group?`)) return
                          try {
                            await api.delete(`/chat/rooms/${activeRoom.id}/members/${m.id}`)
                            setActiveRoom((prev: any) => prev ? {
                              ...prev,
                              members: prev.members.filter((mem: any) => mem.id !== m.id)
                            } : prev)
                          } catch { toast.error('Failed to remove member') }
                        }}
                        className="opacity-0 group-hover/member:opacity-100 text-white/30 hover:text-red-400 transition-all flex-shrink-0"
                        title="Remove from group">
                        <X size={11} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {/* Add member */}
              <div className="px-4 py-2 border-t border-white/5">
                <div className="relative">
                  <select
                    onChange={async (e) => {
                      const userId = e.target.value
                      if (!userId) return
                      try {
                        const res = await api.post(`/chat/rooms/${activeRoom.id}/members`, { userId })
                        setActiveRoom((prev: any) => prev ? {
                          ...prev,
                          members: [...prev.members, res.data.member]
                        } : prev)
                        e.target.value = ''
                        toast.success('Member added!')
                      } catch (err: any) {
                        toast.error(err.response?.data?.error || 'Failed to add member')
                      }
                    }}
                    className="w-full bg-white/10 text-white text-xs rounded-lg px-3 py-1.5 border border-white/10 focus:outline-none focus:border-orange-400 appearance-none">
                    <option value="">+ Add member...</option>
                    {staff.filter(s => !activeRoom.members.some((m: any) => m.id === s.id)).map((s: any) => (
                      <option key={s.id} value={s.id} className="bg-navy-900 text-white">{s.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Pinned messages panel */}
          {view === 'chat' && showPins && pinnedMessages.length > 0 && (
            <div className="bg-[#0d2d4f] border-b border-white/10 flex-shrink-0 max-h-36 overflow-y-auto">
              {pinnedMessages.map(p => (
                <div key={p.id} className="px-4 py-2 flex items-start gap-2 hover:bg-white/5 transition-colors cursor-pointer"
                  onClick={() => {
                    // Scroll to the message in the chat
                    const msgEl = document.getElementById(`chat-msg-${p.messageId}`)
                    if (msgEl) {
                      msgEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
                      // Highlight briefly
                      msgEl.classList.add('ring-2', 'ring-yellow-400', 'ring-offset-1', 'rounded-2xl')
                      setTimeout(() => msgEl.classList.remove('ring-2', 'ring-yellow-400', 'ring-offset-1', 'rounded-2xl'), 2000)
                      setShowPins(false)
                    }
                  }}>
                  <span className="text-yellow-300 text-xs flex-shrink-0 mt-0.5">📌</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-white/50 text-[10px] mb-0.5">{p.senderName}</p>
                    <p className="text-white text-xs truncate">{p.messageText}</p>
                  </div>
                  <button onClick={async (e) => {
                    e.stopPropagation()
                    await api.post(`/chat/rooms/${activeRoom!.id}/pin/${p.messageId}`)
                    api.get(`/chat/rooms/${activeRoom!.id}/pins`).then(res => setPinnedMessages(res.data))
                  }} className="text-white/30 hover:text-white/70 text-xs flex-shrink-0 p-1">✕</button>
                </div>
              ))}
            </div>
          )}

          {/* Search bar */}
          {view === 'chat' && showSearch && (
            <div className="bg-[#0d2d4f] px-4 py-2 flex items-center gap-2 flex-shrink-0">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-white/40 flex-shrink-0"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
              <input
                autoFocus
                className="flex-1 bg-transparent text-white text-sm placeholder-white/30 outline-none"
                placeholder="Search messages..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="text-white/40 hover:text-white">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
                </button>
              )}
            </div>
          )}

          {/* Rooms list */}
          {view === 'rooms' && (
            <div className="flex-1 overflow-y-auto">
              {rooms.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-3 p-6">
                  <MessageCircle size={36} className="opacity-30" />
                  <p className="text-sm text-center">No conversations yet. Start a new message!</p>
                  <button onClick={() => { setView('new-dm'); loadStaff() }}
                    className="text-xs bg-orange-500 text-white px-4 py-2 rounded-xl">
                    Start Messaging
                  </button>
                </div>
              ) : (
                rooms.map(room => (
                  <div key={room.id} onClick={() => openRoom(room)}
                    className={`flex items-center gap-3 px-4 py-3 cursor-pointer border-b border-gray-50 dark:border-[#1e3a5f] transition-colors relative ${room.unreadCount > 0 ? 'bg-orange-50/40 dark:bg-orange-900/10 hover:bg-orange-50/60' : 'hover:bg-gray-50 dark:hover:bg-[#1a3050]'}`}>
                    {room.unreadCount > 0 && <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-orange-500 rounded-r" />}
                    <div className="relative flex-shrink-0">
                      <div className={`w-10 h-10 rounded-2xl flex items-center justify-center text-white font-bold text-sm ${room.type === 'group' ? 'bg-gradient-to-br from-purple-500 to-purple-700' : 'bg-gradient-to-br from-orange-400 to-orange-600'}`}>
                        {room.type === 'group' ? <Users size={15} /> : room.name[0]?.toUpperCase()}
                      </div>
                      {room.type === 'direct' && room.otherUser?.isOnline && (
                        <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-400 rounded-full border-2 border-white dark:border-[#0d1e35]" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className={`text-sm truncate ${room.unreadCount > 0 ? 'font-bold text-navy-900 dark:text-white' : 'font-semibold text-navy-800 dark:text-white'}`}>{room.name}</p>
                        {room.lastMessage && (
                          <span className={`text-[10px] flex-shrink-0 ${room.unreadCount > 0 ? 'text-orange-500 font-semibold' : 'text-gray-400'}`}>{formatTime(room.lastMessage.createdAt)}</span>
                        )}
                      </div>
                      {room.lastMessage && (
                        <p className={`text-xs truncate mt-0.5 ${room.unreadCount > 0 ? 'text-navy-700 dark:text-gray-300 font-medium' : 'text-gray-400'}`}>
                          {room.type === 'group' && <span className="text-gray-400">{room.lastMessage.senderName}: </span>}{room.lastMessage.text}
                        </p>
                      )}
                    </div>
                    {room.unreadCount > 0 && (
                      <span className="w-5 h-5 bg-orange-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
                        {room.unreadCount > 9 ? '9+' : room.unreadCount}
                      </span>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {/* Chat view */}
          {view === 'chat' && (
            <div className="flex flex-col flex-1 overflow-hidden relative">
              <div ref={scrollContainerRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-2"
                onScroll={e => {
                  const el = e.currentTarget
                  setShowScrollToBottom(el.scrollHeight - el.scrollTop - el.clientHeight > 100)
                }}>
                {loadingMessages ? (
                  <div className="flex items-center justify-center h-full text-gray-400 text-sm">Loading...</div>
                ) : messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full gap-3 text-gray-400">
                    <div className="w-14 h-14 bg-gray-100 dark:bg-[#1a3050] rounded-2xl flex items-center justify-center">
                      <MessageCircle size={24} className="text-gray-300 dark:text-gray-600" />
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">No messages yet</p>
                      <p className="text-xs text-gray-400 mt-0.5">Be the first to say something! 👋</p>
                    </div>
                  </div>
                ) : (
                  (() => {
                    const filtered = messages.filter(msg => !searchQuery || msg.messageText?.toLowerCase().includes(searchQuery.toLowerCase()))
                    const elements: React.ReactNode[] = []
                    let lastDate: Date | null = null
                    let lastSenderId: string | null = null
                    let lastMsgTime: Date | null = null

                    filtered.forEach((msg, idx) => {
                      const msgDate = new Date(msg.createdAt)
                      const nextMsg = filtered[idx + 1]
                      const prevMsg = filtered[idx - 1]

                      // Date separator
                      if (!lastDate || !isSameDay(lastDate, msgDate)) {
                        lastDate = msgDate
                        lastSenderId = null
                        elements.push(
                          <div key={`date-${msg.id}`} className="flex items-center gap-3 my-3">
                            <div className="flex-1 h-px bg-gray-100 dark:bg-[#1e3a5f]" />
                            <span className="text-[10px] font-medium text-gray-400 bg-white dark:bg-[#0d1e35] px-2">{formatDateSeparator(msgDate)}</span>
                            <div className="flex-1 h-px bg-gray-100 dark:bg-[#1e3a5f]" />
                          </div>
                        )
                      }

                      // Group detection
                      const timeDiff = lastMsgTime ? (msgDate.getTime() - lastMsgTime.getTime()) / 1000 / 60 : 999
                      const isGrouped = lastSenderId === msg.senderId && timeDiff < 3 && !msg.replyToMessage
                      const isLastInGroup = !nextMsg || nextMsg.senderId !== msg.senderId ||
                        (new Date(nextMsg.createdAt).getTime() - msgDate.getTime()) / 1000 / 60 > 3

                      lastSenderId = msg.senderId
                      lastMsgTime = msgDate

                      // Bubble corner rounding
                      const bubbleRadius = msg.isMine
                        ? `${isGrouped ? 'rounded-2xl rounded-tr-md' : 'rounded-2xl rounded-tr-md'} ${isLastInGroup ? 'rounded-br-sm' : ''}`
                        : `${isGrouped ? 'rounded-2xl rounded-tl-md' : 'rounded-2xl rounded-tl-md'} ${isLastInGroup ? 'rounded-bl-sm' : ''}`

                      elements.push(
                    <div key={msg.id} id={`chat-msg-${msg.id}`} className={`flex ${msg.isMine ? 'justify-end' : 'justify-start'} group ${isGrouped ? 'mt-0.5' : 'mt-2'} transition-all duration-300`}
                      onMouseEnter={() => setHoveredMsgId(msg.id)}
                      onMouseLeave={() => setHoveredMsgId(null)}>
                      <div className={`max-w-[75%] min-w-0 ${msg.isMine ? 'items-end' : 'items-start'} flex flex-col`}>
                        {!msg.isMine && !isGrouped && (
                          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 px-1">{msg.senderName}</p>
                        )}
                        <div className={`relative px-3 py-2 w-full ${bubbleRadius} ${
                          msg.isDeleted ? 'bg-gray-100 dark:bg-[#1a3050] italic text-gray-400 rounded-2xl' :
                          msg.isMine ? 'bg-orange-500 text-white' : 'bg-gray-100 dark:bg-[#1a3050] text-navy-800 dark:text-white'
                        }`}>
                          {/* Quoted reply preview */}
                          {msg.replyToMessage && !msg.isDeleted && (
                            <div className={`mb-2 px-2 py-1.5 rounded-lg border-l-[3px] ${
                              msg.isMine 
                                ? 'border-white/60 bg-black/20' 
                                : 'border-orange-500 bg-white dark:bg-[#0d1e35]'
                            }`}>
                              <p className={`text-[10px] font-bold mb-0.5 ${msg.isMine ? 'text-white/80' : 'text-orange-500'}`}>
                                {msg.replyToMessage.senderName}
                              </p>
                              <p className={`text-xs break-words ${msg.isMine ? 'text-white/60' : 'text-gray-600 dark:text-gray-300'}`}>
                                {msg.replyToMessage.imageUrl ? '📷 Image' : msg.replyToMessage.messageText}
                              </p>
                            </div>
                          )}
                          {msg.isDeleted ? (
                            <p className="text-xs">🗑 Message deleted</p>
                          ) : msg.imageUrl ? (
                            <img src={msg.imageUrl} alt="shared" className="rounded-xl max-w-full cursor-pointer"
                              onClick={() => setImagePreview(msg.imageUrl)} />
                          ) : (
                            <p className="text-sm whitespace-pre-wrap break-words">
                              {msg.messageText ? (searchQuery ? (
                                msg.messageText.split(new RegExp(`(${searchQuery})`, 'gi')).map((part, i) =>
                                  part.toLowerCase() === searchQuery.toLowerCase()
                                    ? <mark key={i} className="bg-yellow-300 text-navy-900 rounded px-0.5">{part}</mark>
                                    : part
                                )
                              ) : renderMessage(msg.messageText)) : null}
                            </p>
                          )}
                          {msg.isMine && !msg.isDeleted && (
                            <button
                              onClick={() => handleDelete(msg.id)}
                              className="absolute -left-6 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-400 transition-all">
                              <Trash2 size={12} />
                            </button>
                          )}
                        </div>
                        {/* Reactions display */}
                        {msg.reactions && Object.keys(msg.reactions).length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1 px-1">
                            {Object.entries(msg.reactions).map(([emoji, data]: any) => (
                              <button key={emoji}
                                onClick={() => api.post(`/chat/messages/${msg.id}/react`, { emoji }).then(() => fetchMessages(activeRoom!.id))}
                                className={`flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full border transition-colors ${
                                  data.isMine ? 'bg-orange-50 border-orange-200 dark:bg-orange-900/20 dark:border-orange-700' : 'bg-gray-50 border-gray-200 dark:bg-[#1a3050] dark:border-[#1e3a5f]'
                                }`}
                                title={data.users.join(', ')}>
                                {emoji} <span className="text-gray-500 dark:text-gray-400">{data.count}</span>
                              </button>
                            ))}
                          </div>
                        )}
                        <div className="flex items-center gap-1 mt-0.5 px-1">
                          {isLastInGroup && <p className="text-xs text-gray-400">{formatTime(msg.createdAt)}</p>}
                          {msg.isMine && !msg.isDeleted && (
                            <span className={`text-xs ${msg.isRead ? 'text-orange-500' : 'text-gray-300'}`}>
                              {msg.isRead ? '✓✓' : '✓'}
                            </span>
                          )}
                          {/* Pin + Reaction picker — show on hover */}
                          {hoveredMsgId === msg.id && !msg.isDeleted && (
                            <div className="flex gap-1 ml-1">
                              <button
                                onClick={() => setReplyingTo(msg)}
                                className="flex items-center gap-0.5 text-[10px] font-medium bg-gray-100 dark:bg-[#112240] hover:bg-orange-100 dark:hover:bg-orange-900/20 text-gray-500 hover:text-orange-500 px-1.5 py-0.5 rounded-lg transition-colors"
                                title="Reply">
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 17 4 12 9 7"/><path d="M20 18v-2a4 4 0 0 0-4-4H4"/></svg>
                                Reply
                              </button>
                              <button
                                onClick={() => api.post(`/chat/rooms/${activeRoom!.id}/pin/${msg.id}`).then(() => {
                                  api.get(`/chat/rooms/${activeRoom!.id}/pins`).then(res => { setPinnedMessages(res.data); setShowPins(true) })
                                })}
                                className="text-xs hover:scale-125 transition-transform opacity-60 hover:opacity-100"
                                title="Pin message">
                                📌
                              </button>
                              {['👍', '✅', '🔥', '❤️', '😊'].map(emoji => (
                                <button key={emoji}
                                  onClick={() => api.post(`/chat/messages/${msg.id}/react`, { emoji }).then(() => fetchMessages(activeRoom!.id))}
                                  className="text-xs hover:scale-125 transition-transform opacity-60 hover:opacity-100">
                                  {emoji}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                      )
                    })
                    return elements
                  })()
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Scroll to bottom */}
              {showScrollToBottom && (
                <div className="absolute bottom-24 right-4 z-10">
                  <button onClick={() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })}
                    className="w-8 h-8 bg-white dark:bg-[#1c3a5e] border border-gray-200 dark:border-[#1e3a5f] rounded-full shadow-md flex items-center justify-center text-gray-500 hover:text-orange-500 transition-colors">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
                  </button>
                </div>
              )}

              {/* Input */}
              <div className="border-t border-gray-100 dark:border-[#1e3a5f] flex-shrink-0">
                {replyingTo && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-orange-50 dark:bg-orange-900/10 border-b border-orange-100 dark:border-orange-900/20">
                    <div className="w-0.5 h-8 bg-orange-400 rounded-full flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] font-semibold text-orange-500">Replying to {replyingTo.senderName}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{replyingTo.imageUrl ? '📷 Image' : replyingTo.messageText}</p>
                    </div>
                    <button onClick={() => setReplyingTo(null)} className="text-gray-400 hover:text-red-400 flex-shrink-0 p-1">
                      <X size={12} />
                    </button>
                  </div>
                )}
              <div className="px-3 py-3 flex items-center gap-2 relative">
                {showJobSuggestions && (
                  <div className="absolute bottom-full left-0 right-0 mb-1 bg-white dark:bg-[#1a3050] border border-gray-100 dark:border-[#1e3a5f] rounded-xl shadow-lg overflow-hidden z-10">
                    {jobSuggestions.map(job => (
                      <div key={job.id} onClick={() => selectJob(job)}
                        className="flex items-center gap-3 px-3 py-2.5 hover:bg-orange-50 dark:hover:bg-[#112240] cursor-pointer border-b border-gray-50 dark:border-[#1e3a5f] last:border-0 transition-colors">
                        <span className="text-xs font-mono font-semibold text-orange-500">{job.jobCode}</span>
                        <span className="text-xs text-gray-600 dark:text-gray-300 truncate flex-1">{job.displayTitle}</span>
                        <span className="text-xs text-gray-400 flex-shrink-0">{job.status}</span>
                      </div>
                    ))}
                  </div>
                )}
                <input type="file" ref={fileInputRef} accept="image/*" onChange={handleImageUpload} className="hidden" />
                <button onClick={() => fileInputRef.current?.click()}
                  className="text-gray-400 hover:text-orange-500 transition-colors flex-shrink-0">
                  <Image size={18} />
                </button>
                {/* Emoji picker */}
                <div className="relative flex-shrink-0" ref={emojiPickerRef}>
                  <button onClick={() => setShowEmojiPicker(p => !p)}
                    className="text-gray-400 hover:text-orange-500 transition-colors">
                    <span className="text-lg leading-none">😊</span>
                  </button>
                  {showEmojiPicker && (
                    <div className="absolute bottom-10 left-0 bg-white dark:bg-[#1c3a5e] border border-gray-100 dark:border-[#1e3a5f] rounded-2xl shadow-xl p-3 w-64 z-50 max-h-64 overflow-y-auto">
                      <div className="grid grid-cols-8 gap-1">
                        {EMOJIS.map(emoji => (
                          <button key={emoji}
                            onClick={() => {
                              setText(prev => prev + emoji)
                              setShowEmojiPicker(false)
                            }}
                            className="text-xl hover:bg-orange-50 rounded-lg p-1 transition-colors leading-none">
                            {emoji}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                {/* @ Mention dropdown */}
                {showMentions && (
                  <div className="absolute bottom-14 left-0 right-0 bg-white dark:bg-[#1c3a5e] border border-gray-100 dark:border-[#1e3a5f] rounded-xl shadow-xl z-50 overflow-hidden">
                    <p className="px-3 py-1.5 text-xs text-gray-400 border-b border-gray-100 dark:border-[#1e3a5f]">Mention someone</p>
                    {mentionSuggestions.map((s: any) => (
                      <div key={s.id} onClick={() => selectMention(s)}
                        className="flex items-center gap-2 px-3 py-2 hover:bg-orange-50 dark:hover:bg-[#1a3050] cursor-pointer">
                        <div className="w-6 h-6 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-xs text-orange-600 font-bold">{s.name[0]}</span>
                        </div>
                        <span className="text-sm text-navy-800 dark:text-white">{s.name}</span>
                        <span className="text-xs text-gray-400 ml-auto capitalize">{s.roleName}</span>
                      </div>
                    ))}
                  </div>
                )}
                {typingUsers.length > 0 && (
                  <div className="px-4 py-1 flex items-center gap-2">
                    <div className="flex gap-0.5">
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                    <span className="text-xs text-gray-400">
                      {typingUsers.length === 1 ? `${typingUsers[0]} is typing...` : `${typingUsers.join(', ')} are typing...`}
                    </span>
                  </div>
                )}
                <textarea
                    ref={textareaRef}
                    value={text}
                    onChange={e => handleTextChange(e.target.value)}
                    onKeyDown={e => {
                      if (showMentions && e.key === 'Escape') { setShowMentions(false); return }
                      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend() }
                    }}
                    placeholder="Type a message... (@ to mention)"
                    rows={1}
                    style={{ resize: 'none', maxHeight: '80px', overflowY: 'auto' }}
                    onInput={e => {
                      const el = e.target as HTMLTextAreaElement
                      el.style.height = 'auto'
                      el.style.height = Math.min(el.scrollHeight, 80) + 'px'
                    }}
                    className="flex-1 bg-gray-50 dark:bg-[#1a3050] border border-gray-100 dark:border-[#1e3a5f] rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-orange-400 transition-colors"
                  />
                <button onClick={handleSend} disabled={sending || !text.trim()}
                  className="w-8 h-8 bg-orange-500 hover:bg-orange-600 disabled:opacity-40 text-white rounded-xl flex items-center justify-center flex-shrink-0 transition-colors">
                  <Send size={14} />
                </button>
              </div>
              </div>
            </div>
          )}

          {/* New DM */}
          {view === 'new-dm' && (
            <div className="flex-1 overflow-y-auto">
              <div className="px-4 py-3 border-b border-gray-50 dark:border-[#1e3a5f]">
                <p className="text-xs text-gray-400">Select a staff member to message</p>
              </div>
              {staff.map(s => (
                <div key={s.id} onClick={() => startDM(s.id)}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-[#1a3050] cursor-pointer border-b border-gray-50 dark:border-[#1e3a5f] transition-colors">
                  <div className="relative flex-shrink-0">
                    <div className="w-9 h-9 bg-navy-100 dark:bg-navy-800 rounded-full flex items-center justify-center">
                      <span className="text-navy-600 dark:text-white font-semibold text-sm">{s.name[0]}</span>
                    </div>
                    {s.isOnline && <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-400 rounded-full border-2 border-white dark:border-[#0d1e35]" />}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-navy-800 dark:text-white">{s.name}</p>
                    <p className="text-xs text-gray-400">{s.roleName}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* New Group */}
          {view === 'new-group' && (
            <div className="flex-1 overflow-y-auto flex flex-col">
              <div className="px-4 py-3 border-b border-gray-50 dark:border-[#1e3a5f] space-y-2 flex-shrink-0">
                <input
                  className="w-full bg-gray-50 dark:bg-[#1a3050] border border-gray-100 dark:border-[#1e3a5f] rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-orange-400"
                  placeholder="Group name..."
                  value={groupName}
                  onChange={e => setGroupName(e.target.value)}
                />
                <p className="text-xs text-gray-400">Select members:</p>
              </div>
              <div className="flex-1 overflow-y-auto">
                {staff.map(s => (
                  <div key={s.id} onClick={() => setSelectedMembers(prev =>
                    prev.includes(s.id) ? prev.filter(id => id !== s.id) : [...prev, s.id]
                  )}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-[#1a3050] cursor-pointer border-b border-gray-50 dark:border-[#1e3a5f] transition-colors">
                    <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                      selectedMembers.includes(s.id) ? 'bg-orange-500 border-orange-500' : 'border-gray-300'
                    }`}>
                      {selectedMembers.includes(s.id) && <span className="text-white text-xs font-bold">✓</span>}
                    </div>
                    <div className="w-8 h-8 bg-navy-100 dark:bg-navy-800 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-navy-600 dark:text-white font-semibold text-xs">{s.name[0]}</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-navy-800 dark:text-white">{s.name}</p>
                      <p className="text-xs text-gray-400">{s.roleName}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="px-4 py-3 border-t border-gray-100 dark:border-[#1e3a5f] flex-shrink-0">
                <button onClick={createGroup} disabled={!groupName.trim() || selectedMembers.length === 0}
                  className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white text-sm font-semibold py-2.5 rounded-xl transition-colors">
                  Create Group ({selectedMembers.length} members)
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Image preview modal */}
      {imagePreview && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4" onClick={() => setImagePreview(null)}>
          <img src={imagePreview} alt="preview" className="max-w-full max-h-full rounded-xl" />
          <button className="absolute top-4 right-4 text-white bg-black/50 rounded-full p-2">
            <X size={20} />
          </button>
        </div>
      )}
    </>
  )
}
